import React from 'react';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import SettingSwitch from '@components/2-molecules/SettingSwitch';
import useSnackbar from '@hooks/useSnackbar';
import { useNotificationPreferences } from '@hooks/notifications/useNotificationPreferences';
import { useNotificationActions } from '@hooks/notifications/useNotificationActions';
import { Frame } from '../Settings.styled';
import { Description, Wrapper } from './NotificationsSettings.styled';

/**
 * Settings tab letting users globally enable/disable notification channels.
 *
 * <p>v1: per-channel global toggle (email, in-app). Per-type granular control
 * is intentionally deferred until product validates the matrix.</p>
 */
const NotificationsSettings: React.FC = () => {
  const { showError } = useSnackbar();
  const { data: prefs, isLoading } = useNotificationPreferences();
  const { updatePreferences } = useNotificationActions();

  const emailEnabled = prefs?.emailEnabled ?? true;
  const inAppEnabled = prefs?.inAppEnabled ?? true;

  const handleToggle = (next: { emailEnabled: boolean; inAppEnabled: boolean }) => {
    updatePreferences.mutate(next, {
      onError: () => showError('Failed to update notification preferences'),
    });
  };

  return (
    <Wrapper data-testid="notifications-settings">
      <PageHeader
        dataTestId="notifications-settings__header"
        variant={PAGE_HEADER_VARIANT_SMALL}
        title="Notifications"
      />
      <Description>
        Choose how you want to be notified. In-app notifications appear in the
        profile menu at the top right of every page; email notifications are
        delivered to your registered email address.
      </Description>

      <Frame>
        <SettingSwitch
          dataTestId="notifications-settings__in-app"
          label="In-app notifications"
          supportingText="Show real-time notifications and history in your profile menu."
          switchProps={{
            enabled: inAppEnabled,
            disabled: isLoading || updatePreferences.isPending,
            onChange: (next: boolean) =>
              handleToggle({ emailEnabled, inAppEnabled: next }),
          }}
        />
        <SettingSwitch
          dataTestId="notifications-settings__email"
          label="Email notifications"
          supportingText="Send notifications to your registered email address."
          switchProps={{
            enabled: emailEnabled,
            disabled: isLoading || updatePreferences.isPending,
            onChange: (next: boolean) =>
              handleToggle({ emailEnabled: next, inAppEnabled }),
          }}
        />
      </Frame>
    </Wrapper>
  );
};

export default NotificationsSettings;
